import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/store/authStore'

export interface QueryDef<TData, TParams extends unknown[] = []> {
  queryKey: (...args: TParams) => readonly unknown[]
  queryFn: (...args: TParams) => Promise<TData>
  staleTime?: number
  gcTime?: number
  auth: 'required' | 'admin' | 'public'
  refetchInterval?: number | false | (() => number | false)
  refetchIntervalInBackground?: boolean
}

/** Per-call overrides, passed as the optional trailing arg after any params. */
interface DefinedQueryOptions {
  enabled?: boolean
  staleTime?: number
  gcTime?: number
}

const DEFINED_QUERY_OPTION_KEYS = new Set<string>(['enabled', 'staleTime', 'gcTime'])

// The trailing arg is the options bag (not a query param) iff it's a plain,
// non-array object whose keys are ALL known option keys. This replaces the old
// `'enabled' in last && Object.keys(last).length === 1` sniff, which both capped
// options to a single `enabled` key AND misread any single-key `{ enabled }`
// passed as a real param. Membership-based detection allows multi-key options
// and only ever misfires on a param that is an object composed *entirely* of
// option-named keys — query params here are ids/numbers/strings/flags, never that.
function isDefinedQueryOptions(value: unknown): value is DefinedQueryOptions {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false
  const keys = Object.keys(value)
  return keys.length > 0 && keys.every((k) => DEFINED_QUERY_OPTION_KEYS.has(k))
}

/** Separate the positional query params from the optional trailing options object. */
function splitParamsAndOptions<TParams extends unknown[]>(
  rest: unknown[],
): { args: TParams; options: DefinedQueryOptions } {
  const last = rest[rest.length - 1]
  if (rest.length > 0 && isDefinedQueryOptions(last)) {
    return { args: rest.slice(0, -1) as unknown as TParams, options: last }
  }
  return { args: rest as unknown as TParams, options: {} }
}

/** Auth gate for a def, combined with the caller's own `enabled` option. */
function isQueryEnabled(
  auth: QueryDef<unknown, unknown[]>['auth'],
  isAuthenticated: boolean,
  user: { admin?: boolean } | null,
  optionEnabled: boolean | undefined,
): boolean {
  let enabled = true
  if (auth === 'required') {
    enabled = isAuthenticated
  } else if (auth === 'admin') {
    enabled = isAuthenticated && user?.admin === true
  }
  return optionEnabled === undefined ? enabled : enabled && optionEnabled
}

/**
 * Generic hook that executes a QueryDef with automatic auth gating.
 *
 * Reads the `auth` field from the definition:
 * - 'public'   → no auth check
 * - 'required' → enabled only when authenticated
 * - 'admin'    → enabled only when authenticated AND user.admin === true
 *
 * Pass the def's query params positionally, then an optional trailing
 * {@link DefinedQueryOptions} bag (`enabled`/`staleTime`/`gcTime`), which
 * combines with the auth gate and overrides the def's own staleTime/gcTime.
 */
export function useDefinedQuery<TData, TParams extends unknown[]>(
  def: QueryDef<TData, TParams>,
  ...rest: [...TParams] | [...TParams, DefinedQueryOptions]
) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const user = useAuthStore((s) => s.user)

  const { args, options } = splitParamsAndOptions<TParams>(rest)
  const enabled = isQueryEnabled(def.auth, isAuthenticated, user, options.enabled)

  return useQuery({
    queryKey: def.queryKey(...args),
    queryFn: () => def.queryFn(...args),
    staleTime: options.staleTime ?? def.staleTime,
    gcTime: options.gcTime ?? def.gcTime,
    refetchInterval: def.refetchInterval,
    refetchIntervalInBackground: def.refetchIntervalInBackground,
    enabled,
  })
}
